<script lang="ts">
  import EmblaCarousel from "$lib/components/EmblaCarousel/EmblaCarousel.svelte";
  import EmblaSlide from "$lib/components/EmblaCarousel/EmblaSlide.svelte";
  import Picture from "$lib/components/Picture/Picture.svelte";
  import Typography from "$lib/components/Typography/Typography.svelte";
  import Icon from "$lib/components/Icon/Icon.svelte";
  import { Position, Alignment, Speed } from "$lib/gql/genCms/codegen";
  import type {
    CardEdge,
    DynamicGridCardContent,
  } from "$lib/gql/genCms/codegen";
  import Link from "../Link/Link.svelte";
  import inView from "$lib/utils/inView";
  import { dynamicGridImageConfig } from "./DynamicGrid.image-config";
  import { onMount } from "svelte";

  export let cards: CardEdge[] = [];
  export let autoscroll: boolean = true;
  export let className: string = "";

  $: cardContent = cards.map(
    card => card.node.content.content,
  ) as DynamicGridCardContent[];

  export let speed: Speed = Speed.Medium; // 'slow', 'medium', 'fast'
  export let showControls: boolean = autoscroll;

  let emblaApi: any;
  let emblaCarousel: any;
  let isPlaying: boolean = true;

  const getPositionClass = (position: Position): string => {
    switch (position) {
      case "LEFT":
        return "dynamic-grid__content--left";
      case "RIGHT":
        return "dynamic-grid__content--right";
      case "CENTER":
      default:
        return "dynamic-grid__content--center";
    }
  };

  const getAlignmentClass = (alignment: Alignment): string => {
    switch (alignment) {
      case "TOP":
        return "dynamic-grid__content--top";
      case "BOTTOM":
        return "dynamic-grid__content--bottom";
      case "CENTER":
      default:
        return "dynamic-grid__content--middle";
    }
  };

  const getTextAlignmentClass = (textAlignment?: Position): string => {
    if (!textAlignment) return "";
    switch (textAlignment) {
      case "LEFT":
        return "dynamic-grid__content-inner--text-left";
      case "RIGHT":
        return "dynamic-grid__content-inner--text-right";
      case "CENTER":
      default:
        return "dynamic-grid__content-inner--text-center";
    }
  };

  let isInView = true;

  let isMobile = false;
  let viewportWidth: number = 0;
  let mdWidth = 768;
  $: isMobile = viewportWidth < mdWidth;
  $: useAutoplay = autoscroll;

  // Control functions
  const togglePlayPause = () => {
    if (!emblaApi) {
      emblaApi = emblaCarousel?.getApi();
    }
    if (emblaApi) {
      const autoScroll = emblaApi.plugins()?.autoScroll;
      if (autoScroll) {
        if (isPlaying) {
          autoScroll.stop();
        } else {
          autoScroll.play();
        }
        isPlaying = !isPlaying;
      }
    }
  };

  const scrollPrev = () => {
    if (!emblaApi) {
      emblaApi = emblaCarousel?.getApi();
    }
    if (emblaApi) {
      emblaApi.scrollPrev();
      // Pause autoplay when user manually navigates
      const autoScroll = emblaApi.plugins()?.autoScroll;
      if (autoScroll && isPlaying) {
        autoScroll.stop();
        isPlaying = false;
      }
    }
  };

  const scrollNext = () => {
    if (!emblaApi) {
      emblaApi = emblaCarousel?.getApi();
    }
    if (emblaApi) {
      emblaApi.scrollNext();
      // Pause autoplay when user manually navigates
      const autoScroll = emblaApi.plugins()?.autoScroll;
      if (autoScroll && isPlaying) {
        autoScroll.stop();
        isPlaying = false;
      }
    }
  };

  let baseSpeed = isMobile ? 0.35 : 0.7;

  $: scrollSpeed = (() => {
    switch (speed) {
      case Speed.Slow:
        return baseSpeed * 0.5;
      case Speed.Medium:
        return baseSpeed;
      case Speed.Fast:
        return baseSpeed * 1.5;
      default:
        return baseSpeed;
    }
  })();

  // Initialize emblaApi when component mounts
  onMount(() => {
    setTimeout(() => {
      emblaApi = emblaCarousel?.getApi();
      // Set initial play state based on autoplay
      isPlaying = useAutoplay;
    }, 100);
  });
</script>

<style lang="scss">
  .fade-in {
    display: inline-block;
    animation: fadeIn 0.6s ease-out forwards;
    animation-delay: 0.2s;
    opacity: 0;
  }

  .fade-up {
    display: inline-block;
    animation: fadeUp 0.6s ease-out forwards;
    animation-delay: 0.6s;
  }
  .dynamic-grid {
    position: relative;
    width: 100%;
    overflow: hidden;

    &__embla {
      width: 100%;
    }

    /* Global fix for all images in dynamic grid to prevent broken icon */
    :global(img:not([src]):not([srcset])),
    :global(img[src=""]),
    :global(img[src="undefined"]),
    :global(img:before),
    :global(img:after) {
      content: none !important;
      display: none !important;
    }

    &__controls {
      position: absolute;
      top: 1rem;
      right: 1rem;
      z-index: 100;
      display: flex;
      gap: 0.5rem;
      padding: 0.25rem;
      border-radius: 8px;
      // backdrop-filter: blur(10px);
    }

    &__control-btn {
      appearance: none;
      background: transparent;
      border: none;
      border-radius: 50%;
      color: $on-background !important;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2.5rem;
      height: 2.5rem;
      transition: all 0.2s ease;

      &:hover {
        // background: $on-surface-10;
        border-color: $on-background;
      }

      &:active {
        transform: scale(0.95);
      }
    }
  }

  /* Use global scope with higher specificity to override EmblaCarousel defaults */
  :global(.dynamic-grid__embla .embla__slide) {
    flex: 0 0 100% !important;
    min-width: 0 !important;
    height: auto !important;
    // padding: 0 0.25rem;

    /* Mobile: 1 slide */
    @media (min-width: 375px) {
      flex: 0 0 100% !important;
    }

    /* Tablet and up: Make slides slightly smaller to ensure scrollability */
    @media (min-width: 768px) {
      flex: 0 0 50% !important;
    }
  }

  .dynamic-grid {
    /* The wrapper div that has dynamic-grid__slide class */
    &__slide {
      height: 100%;
      width: 100%;
    }

    &__card {
      position: relative;
      width: 100%;
      aspect-ratio: 4/5;
      overflow: hidden;
      background-color: $inverse-surface;
    }

    &__image-wrapper {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: #e1e0dc; /* Default background to prevent flash */

      /* Ensure Picture component fills the wrapper */
      :global(picture) {
        display: block;
        width: 100%;
        height: 100%;
      }

      :global(img) {
        width: 100%;
        height: 100%;
        object-fit: cover;
        /* Initially hide the image to prevent broken icon */
        opacity: 0;
        transition: opacity 0.3s ease-in-out;

        /* Show image only when loaded */
        &.image-loaded {
          opacity: 1;
        }

        /* Alternative: hide broken image icon */
        &:not([src]),
        &[src=""],
        &[src="undefined"] {
          opacity: 0;
          visibility: hidden;
        }
      }

      /* Ensure loaded images are visible */
      :global(.image-loaded img) {
        opacity: 1;
      }
    }

    &__content {
      position: absolute;
      z-index: 50;
      display: flex;
      padding: 2rem;
      width: 100%;
      height: 100%;

      @include breakpoint(md) {
        padding: 3rem;
      }

      @include breakpoint(lg) {
        padding: 4rem;
      }

      // Horizontal positioning
      &--left {
        justify-content: flex-start;
      }

      &--center {
        justify-content: center;
      }

      &--right {
        justify-content: flex-end;
      }

      // Vertical positioning
      &--top {
        align-items: flex-start;
      }

      &--middle {
        align-items: center;
      }

      &--bottom {
        align-items: flex-end;
      }
    }

    &__content-inner {
      display: flex;
      flex-direction: column;
      text-align: inherit;
      max-width: 100%;
      z-index: 50;
      gap: 0.25rem;

      @include breakpoint(md) {
        max-width: 70%;
      }

      // Text alignment classes
      &--text-left {
        text-align: left;
        align-items: flex-start;
      }

      &--text-center {
        text-align: center;
        align-items: center;
      }

      &--text-right {
        text-align: right;
        align-items: flex-end;
      }
    }

    &__title {
      margin: 0;
      color: $inverse-on-surface;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    &__body {
      margin: 0.5rem 0;
      color: $inverse-on-surface;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      word-wrap: break-word;
      overflow-wrap: break-word;
      white-space: normal;
    }

    &__cta {
      align-self: flex-start;
    }

    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    @keyframes fadeUp {
      from {
        transform: translateY(20px);
      }
      to {
        transform: translateY(0);
      }
    }
  }
</style>

<svelte:window bind:innerWidth={viewportWidth} />

<div class="dynamic-grid {className}">
  {#if showControls && useAutoplay}
    <div class="dynamic-grid__controls">
      <!-- <button
        class="dynamic-grid__control-btn"
        on:click={scrollPrev}
        aria-label="Previous slide"
        type="button"
      >
        <Icon icon="chevron_left" iconStyle="rounded" withBorder />
      </button> -->
      <button
        class="dynamic-grid__control-btn"
        on:click={togglePlayPause}
        aria-label={isPlaying ? "Pause slideshow" : "Play slideshow"}
        type="button"
      >
        <Icon
          icon={isPlaying ? "pause" : "play_arrow"}
          iconStyle="rounded"
          color="var(--background)"
          withBorder
        />
      </button>
      <!-- <button
        class="dynamic-grid__control-btn"
        on:click={scrollNext}
        aria-label="Next slide"
        type="button"
      >
        <Icon icon="chevron_right" iconStyle="rounded" withBorder />
      </button> -->
    </div>
  {/if}
  <EmblaCarousel
    bind:this={emblaCarousel}
    className="dynamic-grid__embla"
    loop={true}
    autoplay={useAutoplay}
    autoplayOptions={{
      speed: scrollSpeed,
      delay: 0,
      stopOnInteraction: false,
      stopOnMouseEnter: !isMobile,
      stopOnFocusIn: false,
      playOnInit: true,
      stopOnLastSnap: false,
    }}
    pauseOnHover={!isMobile}
    breakpoints={{
      375: {
        slidesToScroll: 1,
        align: "start",
      },
      768: {
        slidesToScroll: 1,
        align: "start",
      },
      1280: {
        slidesToScroll: 1,
        align: "start",
      },
      1600: {
        slidesToScroll: 1,
        align: "start",
      },
    }}
    navigation={!useAutoplay}
  >
    {#each cardContent as card, i}
      <EmblaSlide className="dynamic-grid__slide">
        <div
          class="dynamic-grid__card"
          use:inView={{ threshold: 0.1 }}
          on:intersect={({ detail }) => {
            if (isInView) {
              return;
            }
            if (detail) {
              isInView = true;
            }
          }}
        >
          <div class="dynamic-grid__image-wrapper">
            <div
              class="dynamic-grid__content {getPositionClass(
                card.position,
              )} {getAlignmentClass(card.alignment)}
            {isInView ? 'fade-up' : ''}
            "
            >
              <div
                class="dynamic-grid__content-inner {getTextAlignmentClass(
                  card.textAlignment,
                )}"
              >
                <Typography
                  variant="h2"
                  theme={card.invert ? "inverse-on-surface" : "on-surface"}
                  ><span class="dynamic-grid__title">{card.title}</span
                  ></Typography
                >
                {#if card.body}
                  <span class="dynamic-grid__body">
                    <Typography
                      variant="body"
                      theme={card.invert ? "inverse-on-surface" : "on-surface"}
                    >
                      {@html card.body.html}
                    </Typography>
                  </span>
                {/if}
                {#if card.cta}
                  <Link
                    href={card.cta.href}
                    underline={false}
                    text={card.cta.text}
                    theme={card.invert ? "inverse-on-surface" : "on-surface"}
                    iconLeft={false}
                    icon="east"
                  />
                {/if}
              </div>
            </div>
            <Picture
              smImage={card.image?.uri}
              mdImage={card.image?.uri}
              altText={card.image?.altText || card.title}
              imageConfig={dynamicGridImageConfig}
              isFirstSlide={i === 0}
            />
          </div>
        </div>
      </EmblaSlide>
    {/each}
  </EmblaCarousel>
</div>
